import { operationNextStep } from './operation-presentation.js';
import { operationSections, focusOperationalSection } from './OperationalNavigation.js';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { operationAttempt, type OperationAttempt } from './operation-attempt.js';
import { OperationReport } from './OperationReport.js';
import { fromTimeInput } from './schedule-presentation.js';
import { OperationCsvImport } from './OperationCsvImport.js';
import { OperationPlanning } from './OperationPlanning.js';
import { OperationResources } from './OperationResources.js';
import { AerodromeLookup } from './AerodromeLookup.js';
import { restoreMissionOrigin } from './mission-origin.js';
import { FlightDateTimeFields } from './FlightDateTimeFields.js';
import { CoordinateField, LegDistance } from './FlightGeometry.js';
import { parseCoordinates, validCoordinates, type Coordinates } from './flight-geometry.js';

interface ReferenceData {
  readonly bases: readonly { id: string; name: string; airportCode: string | null }[];
  readonly aircraft: readonly {
    baseIds?: readonly string[];
    id: string;
    registrationMark: string;
    manufacturer: string;
    model: string;
    commercialPrerequisitesMet: boolean;
  }[];
  readonly coordinators: readonly { userId: string; name: string; email: string }[];
}

export interface OperationDetail {
  readonly id: string;
  readonly referenceCode: string;
  readonly missionName: string | null;
  readonly kind: string;
  readonly state: string;
  readonly executionStatus: string;
  readonly base: { id: string; name: string } | null;
  readonly aircraft: { id: string; registrationMark: string; model: string } | null;
  readonly coordinator: { userId: string; name: string } | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly pendingItems: readonly {
    id: string;
    operationId: string;
    title: string;
    detail: string | null;
    severity: string;
    status: string;
    responsible: { userId: string; name: string } | null;
    dueAt: string | null;
    resolutionNote: string | null;
    resolvedBy: { userId: string; name: string } | null;
    resolvedAt: string | null;
    revision: number;
    createdAt: string;
    updatedAt: string;
  }[];
  readonly coordinationDecisions: readonly {
    id: string;
    result: string;
    justification: string;
    decidedBy: { userId: string; name: string };
    authorityRoleKey: string;
    previousState: string;
    resultingState: string;
    decidedAt: string;
  }[];
  readonly operationalAuthorityDecisions: readonly {
    id: string;
    result: string;
    scope: string;
    justification: string;
    decidedBy: { userId: string; name: string };
    authorityRoleKey: string;
    supersedesDecisionId: string | null;
    decidedAt: string;
  }[];
  readonly checklist: {
    id: string;
    templateName: string;
    templateVersion: number;
    createdAt: string;
    updatedAt: string;
    items: readonly {
      id: string;
      sequence: number;
      title: string;
      required: boolean;
      completed: boolean;
      completedBy: { userId: string; name: string } | null;
      completedAt: string | null;
      notes: string | null;
      revision: number;
      updatedAt: string;
    }[];
  } | null;
  readonly legs: readonly {
    originCoordinates?: Coordinates | null;
    destinationCoordinates?: Coordinates | null;
    id: string;
    sequence: number;
    originCode: string | null;
    originName: string;
    destinationCode: string | null;
    destinationName: string;
    plannedDeparture: string;
    plannedArrival: string;
    revisedDeparture: string | null;
    revisedArrival: string | null;
    actualDeparture: string | null;
    actualArrival: string | null;
    cancelledAt: string | null;
  }[];
  readonly executionEvents: readonly {
    id: string;
    eventType: string;
    operationLegId: string | null;
    eventAt: string;
    note: string | null;
    recordedBy: { userId: string; name: string };
    createdAt: string;
  }[];
  readonly occurrences: readonly {
    id: string;
    occurrenceType: string;
    severity: string;
    operationLegId: string | null;
    occurredAt: string;
    description: string;
    justification: string;
    reportedBy: { userId: string; name: string };
    createdAt: string;
  }[];
  readonly closure: {
    id: string;
    result: string;
    summary: string;
    checklist: readonly Record<string, unknown>[];
    closedBy: { userId: string; name: string };
    closedAt: string;
  } | null;
  readonly amendments: readonly {
    id: string;
    reason: string;
    correction: string;
    createdBy: { userId: string; name: string };
    createdAt: string;
  }[];
}

interface CreationResponse {
  readonly operation: OperationDetail;
  readonly replayed: boolean;
}

interface CoordinationDecisionResponse {
  readonly operation: OperationDetail;
  readonly replayed: boolean;
}

interface CoordinationDecisionAttempt {
  readonly decisionId: string;
  readonly idempotencyKey: string;
  readonly result: 'ready' | 'reopened';
  readonly justification: string;
}

interface OperationalDecisionAttempt {
  readonly decisionId: string;
  readonly idempotencyKey: string;
  readonly result: 'authorized' | 'not_authorized' | 'revoked';
  readonly scope: string;
  readonly justification: string;
  readonly supersedesDecisionId: string | null;
}

interface DraftLeg {
  readonly originCoordinates?: Coordinates | null;
  readonly destinationCoordinates?: Coordinates | null;
  readonly originCoordinateInput?: string;
  readonly destinationCoordinateInput?: string;
  readonly clientId: string;
  readonly originCode: string;
  readonly originName: string;
  readonly destinationCode: string;
  readonly destinationName: string;
  readonly plannedDeparture: string;
  readonly plannedArrival: string;
}

interface OperationDraft {
  readonly missionName: string;
  readonly kind: string;
  readonly baseId: string;
  readonly aircraftId: string;
  readonly assignedCoordinatorUserId: string;
  readonly legs: readonly DraftLeg[];
}

const operationKindOptions = [
  { key: 'commercial', label: 'Comercial' },
  { key: 'private', label: 'Privada' },
  { key: 'corporate', label: 'Corporativa' },
  { key: 'shared', label: 'Compartilhada' },
  { key: 'ferry', label: 'Traslado' },
  { key: 'training', label: 'Treinamento' },
  { key: 'maintenance', label: 'Manutenção' },
  { key: 'support', label: 'Apoio' },
  { key: 'other', label: 'Outra' },
] as const;

const operationStateLabels: Readonly<Record<string, string>> = {
  draft: 'Rascunho',
  planned: 'Planejada',
  coordinating: 'Em coordenação',
  ready: 'Coordenação concluída',
  in_progress: 'Em curso',
  completed: 'Concluída',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
};

const executionStatusLabels: Readonly<Record<string, string>> = {
  not_started: 'Não iniciada',
  in_progress: 'Em execução',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

const executionEventLabels: Readonly<Record<string, string>> = {
  started: 'Execução iniciada',
  departed: 'Partida real registrada',
  arrived: 'Chegada real registrada',
  cancelled: 'Execução cancelada',
};

const occurrenceTypeLabels: Readonly<Record<string, string>> = {
  delay: 'Atraso',
  diversion: 'Desvio de rota',
  cancellation: 'Cancelamento',
  technical: 'Técnica',
  weather: 'Meteorologia',
  passenger: 'Passageiro',
  other: 'Outra',
};

function toLocalDateTime(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function createInitialLeg(start = new Date()): DraftLeg {
  const departure = new Date(start);
  departure.setMinutes(0, 0, 0);
  departure.setHours(departure.getHours() + 1);
  const arrival = new Date(departure.getTime() + 90 * 60_000);
  return {
    clientId: crypto.randomUUID(),
    originCode: '',
    originName: '',
    destinationCode: '',
    destinationName: '',
    plannedDeparture: toLocalDateTime(departure),
    plannedArrival: toLocalDateTime(arrival),
  };
}

function applyInitialSchedule(
  draft: OperationDraft,
  initialSchedule?: { readonly plannedDeparture: string; readonly plannedArrival: string },
): OperationDraft {
  if (!initialSchedule) return draft;
  return {
    ...draft,
    legs: draft.legs.map((leg, index) => (index === 0 ? { ...leg, ...initialSchedule } : leg)),
  };
}

function createInitialDraft(initialSchedule?: {
  readonly plannedDeparture: string;
  readonly plannedArrival: string;
}): OperationDraft {
  return {
    missionName: '',
    kind: 'commercial',
    baseId: '',
    aircraftId: '',
    assignedCoordinatorUserId: '',
    legs: [initialSchedule ? { ...createInitialLeg(), ...initialSchedule } : createInitialLeg()],
  };
}

function readOperationDraft(
  organizationId: string,
  initialSchedule?: { readonly plannedDeparture: string; readonly plannedArrival: string },
): OperationDraft {
  try {
    const stored = sessionStorage.getItem(`aeronexo:operation-draft:v1:${organizationId}`);
    if (!stored) return createInitialDraft(initialSchedule);
    const candidate = JSON.parse(stored) as Partial<OperationDraft>;
    if (
      typeof candidate.missionName !== 'string' ||
      typeof candidate.kind !== 'string' ||
      !Array.isArray(candidate.legs) ||
      candidate.legs.length === 0
    ) {
      return createInitialDraft(initialSchedule);
    }
    return applyInitialSchedule(candidate as OperationDraft, initialSchedule);
  } catch {
    return createInitialDraft(initialSchedule);
  }
}

function responseError(response: Response): Error & { status: number } {
  const error = new Error(`REQUEST_FAILED:${response.status}`) as Error & { status: number };
  error.status = response.status;
  return error;
}

async function getJson<ResponseBody>(url: string): Promise<ResponseBody> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw responseError(response);
  return (await response.json()) as ResponseBody;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function OperationCreator({
  organizationId,
  initialSchedule,
  onCancel,
  onCreated,
  onImported,
  onExpiredSession,
}: {
  readonly organizationId: string;
  readonly initialSchedule?: {
    readonly plannedDeparture: string;
    readonly plannedArrival: string;
  };
  readonly onCancel: () => void;
  readonly onCreated: (operationId: string) => Promise<void> | void;
  readonly onImported: () => Promise<void> | void;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [referenceData, setReferenceData] = useState<ReferenceData>();
  const draftStorageKey = `aeronexo:operation-draft:v1:${organizationId}`;
  const [draft, setDraft] = useState<OperationDraft>(() =>
    readOperationDraft(organizationId, initialSchedule),
  );
  const [submissionIdentity, setSubmissionIdentity] = useState<{
    operationId: string;
    idempotencyKey: string;
  }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [entryMode, setEntryMode] = useState<'manual' | 'csv'>('manual');

  const locationCodes = draft.legs
    .flatMap((leg) => [leg.originCode, leg.destinationCode])
    .map((code) => (/^[A-Z0-9]{3,4}$/.test(code) && code !== 'ZZZZ' ? code : ''))
    .join(',');
  useEffect(() => {
    const controller = new AbortController();
    void Promise.all(
      [...new Set(locationCodes.split(',').filter(Boolean))].map(async (code) => {
        try {
          const response = await fetch(`/v1/reference/aerodromes?q=${encodeURIComponent(code)}`, {
            credentials: 'include',
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10000)]),
          });
          if (!response.ok) return;
          const body = (await response.json()) as {
            results: Array<{
              icaoCode: string | null;
              iataCode: string | null;
              latitude: number | null;
              longitude: number | null;
            }>;
          };
          const point = body.results.find(
            (item) => item.icaoCode === code || item.iataCode === code,
          );
          if (!validCoordinates(point) || controller.signal.aborted) return;
          setDraft((current) => ({
            ...current,
            legs: current.legs.map((leg) => ({
              ...leg,
              ...(leg.originCode === code
                ? { originCoordinates: { latitude: point.latitude, longitude: point.longitude } }
                : {}),
              ...(leg.destinationCode === code
                ? {
                    destinationCoordinates: {
                      latitude: point.latitude,
                      longitude: point.longitude,
                    },
                  }
                : {}),
            })),
          }));
        } catch {
          /* The form remains usable when the catalog is unavailable. */
        }
      }),
    );
    return () => controller.abort();
  }, [locationCodes]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    void getJson<ReferenceData>(`/v1/organizations/${organizationId}/coordination/reference-data`)
      .then((result) => {
        if (active) {
          setReferenceData(result);
          setDraft((current) => restoreMissionOrigin(current, result));
        }
      })
      .catch(async (requestError: unknown) => {
        if (!active) return;
        if ((requestError as { status?: number }).status === 401) {
          await onExpiredSession();
          return;
        }
        setError('Não foi possível carregar aeronaves, bases e coordenadores.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onExpiredSession, organizationId]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      sessionStorage.setItem(draftStorageKey, JSON.stringify(draft));
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [draft, draftStorageKey]);

  const changeDraft = (update: (current: OperationDraft) => OperationDraft) => {
    setDraft((current) => update(current));
    setSubmissionIdentity(undefined);
    setError(undefined);
  };

  const changeLeg = (index: number, changes: Partial<DraftLeg>) => {
    changeDraft((current) => ({
      ...current,
      legs: current.legs.map((leg, legIndex) =>
        legIndex === index ? { ...leg, ...changes } : leg,
      ),
    }));
  };

  const selectBase = (current: OperationDraft, baseId: string): OperationDraft => {
    const base = referenceData?.bases.find((item) => item.id === baseId);
    return {
      ...current,
      baseId: base?.id || '',
      legs: current.legs.map((leg, index) =>
        index === 0
          ? {
              ...leg,
              originCode: base?.airportCode || '',
              originName: base?.name || '',
              originCoordinates: null,
              originCoordinateInput: '',
            }
          : leg,
      ),
    };
  };
  const aircraftBases =
    referenceData?.bases.filter(
      (base) =>
        !draft.aircraftId ||
        referenceData.aircraft
          .find((item) => item.id === draft.aircraftId)
          ?.baseIds?.includes(base.id),
    ) || [];
  const selectAircraft = (aircraftId: string) => {
    changeDraft((current) => {
      const linked =
        referenceData?.bases.filter((base) =>
          referenceData.aircraft.find((item) => item.id === aircraftId)?.baseIds?.includes(base.id),
        ) || [];
      const baseId = linked.some((base) => base.id === current.baseId)
        ? current.baseId
        : linked.length === 1
          ? linked[0]!.id
          : '';
      return selectBase({ ...current, aircraftId }, baseId);
    });
  };

  const addLeg = () => {
    try {
      const arrival = draft.legs.at(-1)?.plannedArrival;
      if (arrival) fromTimeInput(arrival, false);
    } catch {
      setError('Conclua a data e o horário de chegada antes de adicionar o próximo trecho.');
      return;
    }
    changeDraft((current) => {
      const previous = current.legs.at(-1);
      const nextStart = previous?.plannedArrival ? new Date(previous.plannedArrival) : new Date();
      nextStart.setHours(nextStart.getHours() + 1);
      const next = createInitialLeg(nextStart);
      return {
        ...current,
        legs: [
          ...current.legs,
          {
            ...next,
            originCode: previous?.destinationCode ?? '',
            originName: previous?.destinationName ?? '',
            originCoordinates: previous?.destinationCoordinates ?? null,
            originCoordinateInput: previous?.destinationCoordinateInput ?? '',
          },
        ],
      };
    });
  };

  const removeLeg = (index: number) => {
    changeDraft((current) =>
      current.legs.length === 1
        ? current
        : { ...current, legs: current.legs.filter((_, legIndex) => legIndex !== index) },
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      let previousArrival = -Infinity;
      for (const [index, leg] of draft.legs.entries()) {
        for (const side of ['origin', 'destination'] as const) {
          if (
            leg[`${side}Code`] === 'ZZZZ' &&
            !parseCoordinates(leg[`${side}CoordinateInput`] ?? '')
          )
            throw Error(
              `Informe coordenadas válidas para ${side === 'origin' ? 'a origem' : 'o destino'} do trecho ${index + 1}.`,
            );
        }
        const departure = Date.parse(fromTimeInput(leg.plannedDeparture, false));
        const arrival = Date.parse(fromTimeInput(leg.plannedArrival, false));
        if (arrival <= departure)
          throw Error(
            `No trecho ${index + 1}, a chegada deve ocorrer depois da partida. Confira também a data.`,
          );
        if (departure < previousArrival)
          throw Error(
            `No trecho ${index + 1}, a partida deve ocorrer após a chegada do trecho anterior.`,
          );
        previousArrival = arrival;
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Confira as datas e os horários.');
      return;
    }
    setIsSaving(true);
    setError(undefined);
    const identity = submissionIdentity ?? {
      operationId: crypto.randomUUID(),
      idempotencyKey: crypto.randomUUID(),
    };
    setSubmissionIdentity(identity);

    try {
      const response = await fetch(`/v1/organizations/${organizationId}/operations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...identity,
          kind: draft.kind,
          missionName: draft.missionName,
          displayTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          baseId: draft.baseId || null,
          aircraftId: draft.aircraftId || null,
          assignedCoordinatorUserId: draft.assignedCoordinatorUserId || null,
          legs: draft.legs.map((leg) => ({
            originCode: leg.originCode || null,
            originName: leg.originName,
            destinationCode: leg.destinationCode || null,
            destinationName: leg.destinationName,
            originCoordinates:
              leg.originCode === 'ZZZZ'
                ? parseCoordinates(leg.originCoordinateInput ?? '')
                : (leg.originCoordinates ?? null),
            destinationCoordinates:
              leg.destinationCode === 'ZZZZ'
                ? parseCoordinates(leg.destinationCoordinateInput ?? '')
                : (leg.destinationCoordinates ?? null),
            plannedDeparture: fromTimeInput(leg.plannedDeparture, false),
            plannedArrival: fromTimeInput(leg.plannedArrival, false),
          })),
        }),
      });
      if (!response.ok) throw responseError(response);
      const result = (await response.json()) as CreationResponse;
      setSubmissionIdentity(undefined);
      sessionStorage.removeItem(draftStorageKey);
      await onCreated(result.operation.id);
    } catch (requestError) {
      const status = (requestError as { status?: number }).status;
      if (status === 401) {
        await onExpiredSession();
        return;
      }
      setError(
        status === 409
          ? 'Há conflito de horário da aeronave ou de envio. Revise os trechos e os horários antes de tentar novamente.'
          : status === 422
            ? 'A aeronave ainda não possui todas as pré-condições cadastrais para uma operação comercial. Revise EO, operador responsável, disponibilidade e bloqueios no cadastro.'
            : status === 400
              ? 'Revise os trechos, horários e vínculos selecionados.'
              : 'Não foi possível salvar. Os dados foram mantidos; tente novamente.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section
      className="operationComposer"
      id="operation-composer"
      aria-labelledby="operation-composer-title"
    >
      <header className="workflowHeader">
        <div>
          <span className="sectionEyebrow">Planejamento</span>
          <h2 id="operation-composer-title">Nova operação</h2>
          <p>Salve o planejamento com um ou mais trechos. O registro começa como rascunho.</p>
          <small>Seu preenchimento fica preservado nesta guia até a operação ser salva.</small>
        </div>
        <button type="button" onClick={onCancel}>
          Cancelar
        </button>
      </header>

      {isLoading ? (
        <div className="workflowLoading" role="status">
          Carregando dados da organização…
        </div>
      ) : null}
      {error ? (
        <div className="inlineError" role="alert">
          {error}
        </div>
      ) : null}

      {!isLoading && referenceData ? (
        <div className="composerModeSwitch" role="group" aria-label="Forma de entrada">
          <button
            aria-pressed={entryMode === 'manual'}
            className={entryMode === 'manual' ? 'isActive' : undefined}
            type="button"
            onClick={() => setEntryMode('manual')}
          >
            Preencher manualmente
          </button>
          <button
            aria-pressed={entryMode === 'csv'}
            className={entryMode === 'csv' ? 'isActive' : undefined}
            type="button"
            onClick={() => setEntryMode('csv')}
          >
            Importar CSV
          </button>
        </div>
      ) : null}

      {!isLoading && referenceData && entryMode === 'manual' ? (
        <form className="operationForm" onSubmit={(event) => void handleSubmit(event)}>
          <div className="operationFormGrid">
            <label className="fieldWide">
              <span>Identificação da missão</span>
              <input
                maxLength={160}
                minLength={3}
                placeholder="Ex.: Executivo Rio — São Paulo"
                required
                value={draft.missionName}
                onChange={(event) =>
                  changeDraft((current) => ({ ...current, missionName: event.target.value }))
                }
              />
            </label>
            <label>
              <span>Tipo de operação</span>
              <select
                value={draft.kind}
                onChange={(event) =>
                  changeDraft((current) => ({ ...current, kind: event.target.value }))
                }
              >
                {operationKindOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Aeronave</span>
              <select
                value={draft.aircraftId}
                onChange={(event) => selectAircraft(event.target.value)}
              >
                <option value="">Atribuir depois</option>
                {referenceData.aircraft.map((aircraft) => (
                  <option
                    disabled={draft.kind === 'commercial' && !aircraft.commercialPrerequisitesMet}
                    key={aircraft.id}
                    value={aircraft.id}
                  >
                    {aircraft.registrationMark} · {aircraft.model}
                    {draft.kind === 'commercial' && !aircraft.commercialPrerequisitesMet
                      ? ' · pré-condições pendentes'
                      : ''}
                  </option>
                ))}
              </select>
              {draft.kind === 'commercial' ? (
                <small>
                  Operações comerciais exigem EO aprovada, operador responsável, disponibilidade e
                  ausência de bloqueio técnico informado.
                </small>
              ) : null}
            </label>
            <label>
              <span>Base de partida</span>
              <select
                value={draft.baseId}
                onChange={(event) =>
                  changeDraft((current) => selectBase(current, event.target.value))
                }
              >
                <option value="">
                  {draft.aircraftId
                    ? 'Selecione a base da aeronave'
                    : 'Selecione a aeronave primeiro'}
                </option>
                {aircraftBases.map((base) => (
                  <option key={base.id} value={base.id}>
                    {base.airportCode ? `${base.airportCode} · ` : ''}
                    {base.name}
                  </option>
                ))}
              </select>
              {draft.aircraftId ? (
                <small>
                  {aircraftBases.length > 1
                    ? 'A aeronave atende várias bases. Escolha de onde esta missão partirá.'
                    : aircraftBases.length === 0
                      ? 'Esta aeronave ainda não possui base vinculada. Atualize seu cadastro.'
                      : 'A origem do primeiro trecho foi preenchida com a base da aeronave.'}
                </small>
              ) : null}
            </label>
            <label>
              <span>Coordenador responsável</span>
              <select
                value={draft.assignedCoordinatorUserId}
                onChange={(event) =>
                  changeDraft((current) => ({
                    ...current,
                    assignedCoordinatorUserId: event.target.value,
                  }))
                }
              >
                <option value="">Eu, usuário atual</option>
                {referenceData.coordinators.map((coordinator) => (
                  <option key={coordinator.userId} value={coordinator.userId}>
                    {coordinator.name} · {coordinator.email}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="legCollection">
            <div className="legCollectionHeader">
              <div>
                <span className="sectionEyebrow">Roteiro</span>
                <h3>{draft.legs.length === 1 ? '1 trecho' : `${draft.legs.length} trechos`}</h3>
              </div>
              <button disabled={draft.legs.length >= 12} type="button" onClick={addLeg}>
                Adicionar trecho
              </button>
            </div>
            <p className="planningHint">
              Informe os horários em {Intl.DateTimeFormat().resolvedOptions().timeZone}. Esse fuso
              será registrado com o planejamento. Os horários iniciais são apenas sugestões e
              precisam ser conferidos.
            </p>
            {draft.legs.map((leg, index) => (
              <fieldset className="legEditor" key={leg.clientId}>
                <legend>Trecho {index + 1}</legend>
                <div className="locationLookupGrid">
                  <AerodromeLookup
                    label="Encontrar origem na ANAC"
                    selectedLocation={[leg.originCode, leg.originName].filter(Boolean).join(' — ')}
                    onExpiredSession={onExpiredSession}
                    onSelect={(suggestion) =>
                      changeLeg(index, {
                        originCode: suggestion.icaoCode ?? suggestion.iataCode ?? '',
                        originName: suggestion.name,
                        originCoordinates: validCoordinates(suggestion)
                          ? { latitude: suggestion.latitude, longitude: suggestion.longitude }
                          : null,
                        originCoordinateInput: '',
                      })
                    }
                  />
                  <AerodromeLookup
                    label="Encontrar destino na ANAC"
                    selectedLocation={[leg.destinationCode, leg.destinationName]
                      .filter(Boolean)
                      .join(' — ')}
                    onExpiredSession={onExpiredSession}
                    onSelect={(suggestion) =>
                      changeLeg(index, {
                        destinationCode: suggestion.icaoCode ?? suggestion.iataCode ?? '',
                        destinationName: suggestion.name,
                        destinationCoordinates: validCoordinates(suggestion)
                          ? { latitude: suggestion.latitude, longitude: suggestion.longitude }
                          : null,
                        destinationCoordinateInput: '',
                      })
                    }
                  />
                </div>
                <div className="locationFields">
                  <label>
                    <span>Código de origem</span>
                    <input
                      maxLength={100}
                      placeholder="SBJR ou ZZZZ"
                      value={leg.originCode}
                      onBlur={() => {
                        const point = parseCoordinates(leg.originCode);
                        if (point)
                          changeLeg(index, {
                            originCode: 'ZZZZ',
                            originCoordinateInput: leg.originCode,
                            originCoordinates: point,
                          });
                      }}
                      onChange={(event) =>
                        changeLeg(index, {
                          originCode: event.target.value.toUpperCase(),
                          originCoordinates: null,
                          originCoordinateInput: '',
                        })
                      }
                    />
                  </label>
                  <label className="locationName">
                    <span>Origem</span>
                    <input
                      maxLength={160}
                      minLength={2}
                      placeholder="Nome do aeródromo ou local"
                      required
                      value={leg.originName}
                      onChange={(event) => changeLeg(index, { originName: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Código de destino</span>
                    <input
                      maxLength={100}
                      placeholder="SBSP ou ZZZZ"
                      value={leg.destinationCode}
                      onBlur={() => {
                        const point = parseCoordinates(leg.destinationCode);
                        if (point)
                          changeLeg(index, {
                            destinationCode: 'ZZZZ',
                            destinationCoordinateInput: leg.destinationCode,
                            destinationCoordinates: point,
                          });
                      }}
                      onChange={(event) =>
                        changeLeg(index, {
                          destinationCode: event.target.value.toUpperCase(),
                          destinationCoordinates: null,
                          destinationCoordinateInput: '',
                        })
                      }
                    />
                  </label>
                  <label className="locationName">
                    <span>Destino</span>
                    <input
                      maxLength={160}
                      minLength={2}
                      placeholder="Nome do aeródromo ou local"
                      required
                      value={leg.destinationName}
                      onChange={(event) =>
                        changeLeg(index, { destinationName: event.target.value })
                      }
                    />
                  </label>
                </div>
                <div className="locationLookupGrid">
                  {leg.originCode === 'ZZZZ' ? (
                    <CoordinateField
                      label="origem"
                      value={leg.originCoordinateInput ?? ''}
                      onChange={(value) =>
                        changeLeg(index, {
                          originCoordinateInput: value,
                          originCoordinates: parseCoordinates(value),
                        })
                      }
                    />
                  ) : null}
                  {leg.destinationCode === 'ZZZZ' ? (
                    <CoordinateField
                      label="destino"
                      value={leg.destinationCoordinateInput ?? ''}
                      onChange={(value) =>
                        changeLeg(index, {
                          destinationCoordinateInput: value,
                          destinationCoordinates: parseCoordinates(value),
                        })
                      }
                    />
                  ) : null}
                </div>
                <LegDistance
                  origin={leg.originCoordinates ?? null}
                  destination={leg.destinationCoordinates ?? null}
                />
                <div className="timeFields">
                  <FlightDateTimeFields
                    label="Partida"
                    value={leg.plannedDeparture}
                    onChange={(value) => changeLeg(index, { plannedDeparture: value })}
                  />
                  <FlightDateTimeFields
                    label="Chegada"
                    value={leg.plannedArrival}
                    onChange={(value) => changeLeg(index, { plannedArrival: value })}
                  />
                  {draft.legs.length > 1 ? (
                    <button className="dangerButton" type="button" onClick={() => removeLeg(index)}>
                      Remover trecho
                    </button>
                  ) : null}
                </div>
              </fieldset>
            ))}
            <button disabled={draft.legs.length >= 12} type="button" onClick={addLeg}>
              {draft.legs.length >= 12
                ? 'Limite de 12 trechos atingido'
                : 'Adicionar próximo trecho'}
            </button>
          </div>

          <footer className="workflowActions">
            <button type="button" onClick={onCancel}>
              Cancelar
            </button>
            <button className="primaryButton" disabled={isSaving} type="submit">
              {isSaving ? 'Salvando…' : 'Salvar operação'}
            </button>
          </footer>
        </form>
      ) : null}

      {!isLoading && referenceData && entryMode === 'csv' ? (
        <OperationCsvImport
          organizationId={organizationId}
          referenceData={referenceData}
          onExpiredSession={onExpiredSession}
          onImported={onImported}
        />
      ) : null}
    </section>
  );
}

export function OperationDetailView({
  canEdit,
  active = true,
  operationId,
  organizationId,
  onClose,
  requestedSection,
  onExpiredSession,
}: {
  readonly canEdit: boolean;
  readonly active?: boolean;
  readonly operationId: string;
  readonly organizationId: string;
  readonly onClose: () => void;
  readonly requestedSection?: { readonly section: string; readonly sequence: number } | undefined;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [activeSection, setActiveSection] = useState('operation-summary');
  useEffect(() => {
    if (requestedSection && operationSections.some(([id]) => id === requestedSection.section))
      setActiveSection(requestedSection.section);
  }, [requestedSection]);
  const selectSection = (id: string) => {
    setActiveSection(id);
    requestAnimationFrame(() => focusOperationalSection(id));
  };
  const [operation, setOperation] = useState<OperationDetail>();
  const [members, setMembers] = useState<
    readonly { userId: string; name: string; email: string }[]
  >([]);
  const [error, setError] = useState<string>();
  const [checklistChanges, setChecklistChanges] = useState<Record<string, boolean>>({});
  const checklistRequests = useRef(new Set<string>());
  const checklistScope = useRef('');
  const isSavingChecklist = Object.keys(checklistChanges).length > 0;
  const [updatingPendingItemId, setUpdatingPendingItemId] = useState<string>();
  const [isCreatingPendingItem, setIsCreatingPendingItem] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingDetail, setPendingDetail] = useState('');
  const [pendingSeverity, setPendingSeverity] = useState('warning');
  const [pendingResponsibleUserId, setPendingResponsibleUserId] = useState('');
  const [pendingDueAt, setPendingDueAt] = useState('');
  const [decisionJustification, setDecisionJustification] = useState('');
  const [decisionAttempt, setDecisionAttempt] = useState<CoordinationDecisionAttempt>();
  const [isRecordingDecision, setIsRecordingDecision] = useState(false);
  const [operationalResult, setOperationalResult] = useState<'authorized' | 'not_authorized'>(
    'authorized',
  );
  const [operationalScope, setOperationalScope] = useState(
    'Esta operação e seus trechos planejados',
  );
  const [operationalJustification, setOperationalJustification] = useState('');
  const [operationalAttempt, setOperationalAttempt] = useState<OperationalDecisionAttempt>();
  const [isRecordingOperationalDecision, setIsRecordingOperationalDecision] = useState(false);
  const attempts = useRef(new Map<string, OperationAttempt>());
  const [executionTime, setExecutionTime] = useState('');
  const [executionNote, setExecutionNote] = useState('');
  const [isRecordingExecutionEvent, setIsRecordingExecutionEvent] = useState(false);
  const [occurrenceType, setOccurrenceType] = useState('delay');
  const [occurrenceSeverity, setOccurrenceSeverity] = useState('warning');
  const [occurrenceLegId, setOccurrenceLegId] = useState('');
  const [occurrenceDescription, setOccurrenceDescription] = useState('');
  const [occurrenceJustification, setOccurrenceJustification] = useState('');
  const [isRecordingOccurrence, setIsRecordingOccurrence] = useState(false);
  const closureResult = operation?.executionStatus === 'cancelled' ? 'cancelled' : 'completed';
  const [closureSummary, setClosureSummary] = useState('');
  const [closureChecks, setClosureChecks] = useState({
    execution_recorded: false,
    occurrences_reviewed: false,
    handover_completed: false,
  });
  const [isClosingOperation, setIsClosingOperation] = useState(false);
  const [amendmentReason, setAmendmentReason] = useState('');
  const [amendmentCorrection, setAmendmentCorrection] = useState('');
  const [isCreatingAmendment, setIsCreatingAmendment] = useState(false);

  const loadOperation = useCallback(
    () => getJson<OperationDetail>(`/v1/organizations/${organizationId}/operations/${operationId}`),
    [operationId, organizationId],
  );

  useEffect(() => {
    let active = true;
    checklistScope.current = `${organizationId}:${operationId}`;
    checklistRequests.current.clear();
    setChecklistChanges({});
    setOperation(undefined);
    setError(undefined);
    void loadOperation()
      .then((result) => {
        if (active) {
          setOperation(result);
          setPendingResponsibleUserId(result.coordinator?.userId ?? '');
        }
      })
      .catch(async (requestError: unknown) => {
        if (!active) return;
        if ((requestError as { status?: number }).status === 401) {
          await onExpiredSession();
          return;
        }
        setError('Não foi possível reabrir esta operação.');
      });
    return () => {
      active = false;
      checklistScope.current = '';
    };
  }, [loadOperation, onExpiredSession]);

  useEffect(() => {
    if (!canEdit) return;
    let active = true;
    void getJson<{ members: readonly { userId: string; name: string; email: string }[] }>(
      `/v1/organizations/${organizationId}/shift-handoff`,
    )
      .then((result) => {
        if (active) setMembers(result.members);
      })
      .catch(async (requestError: unknown) => {
        if ((requestError as { status?: number }).status === 401) await onExpiredSession();
      });
    return () => {
      active = false;
    };
  }, [canEdit, onExpiredSession, organizationId]);

  const setChecklistItemCompletion = async (
    item: NonNullable<OperationDetail['checklist']>['items'][number],
    completed: boolean,
  ) => {
    if (!canEdit || checklistRequests.current.has(item.id)) return;
    const scope = checklistScope.current;
    checklistRequests.current.add(item.id);
    setChecklistChanges((current) => ({ ...current, [item.id]: completed }));
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/checklist/items/${item.id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            expectedRevision: item.revision,
            completed,
            notes: item.notes,
          }),
          signal: AbortSignal.timeout(15000),
        },
      );
      if (checklistScope.current !== scope) return;
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        const refreshed = await loadOperation();
        if (checklistScope.current !== scope) return;
        const freshItem = refreshed.checklist?.items.find((candidate) => candidate.id === item.id);
        if (freshItem)
          setOperation((current) =>
            current?.checklist
              ? {
                  ...current,
                  checklist: {
                    ...current.checklist,
                    items: current.checklist.items.map((candidate) =>
                      candidate.id === item.id ? freshItem : candidate,
                    ),
                  },
                }
              : current,
          );
        setError('Este item foi alterado em outra sessão. O checklist foi atualizado.');
        return;
      }
      if (!response.ok) throw responseError(response);
      const { item: updated } = (await response.json()) as {
        item: {
          id: string;
          completed: boolean;
          completedByUserId: string | null;
          completedAt: string | null;
          notes: string | null;
          revision: number;
          updatedAt: string;
        };
      };
      if (checklistScope.current !== scope) return;
      setOperation((current) => {
        if (!current?.checklist) return current;
        const person = members.find((member) => member.userId === updated.completedByUserId);
        return {
          ...current,
          checklist: {
            ...current.checklist,
            items: current.checklist.items.map((candidate) =>
              candidate.id === updated.id
                ? {
                    ...candidate,
                    completed: updated.completed,
                    completedAt: updated.completedAt,
                    notes: updated.notes,
                    revision: updated.revision,
                    updatedAt: updated.updatedAt,
                    completedBy: updated.completedByUserId
                      ? {
                          userId: updated.completedByUserId,
                          name:
                            person?.name ??
                            (candidate.completedBy?.userId === updated.completedByUserId
                              ? candidate.completedBy.name
                              : 'Usuário responsável'),
                        }
                      : null,
                  }
                : candidate,
            ),
          },
        };
      });
    } catch {
      if (checklistScope.current !== scope) return;
      setError('Não foi possível atualizar o item. O valor anterior foi preservado.');
    } finally {
      if (checklistScope.current === scope) {
        checklistRequests.current.delete(item.id);
        setChecklistChanges((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
      }
    }
  };

  const createPendingItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || isCreatingPendingItem) return;
    setIsCreatingPendingItem(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/pending-items`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pendingItemId: crypto.randomUUID(),
            title: pendingTitle,
            detail: pendingDetail || null,
            severity: pendingSeverity,
            responsibleUserId: pendingResponsibleUserId || null,
            dueAt: pendingDueAt ? new Date(pendingDueAt).toISOString() : null,
          }),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw responseError(response);
      setOperation(await loadOperation());
      setPendingTitle('');
      setPendingDetail('');
      setPendingSeverity('warning');
      setPendingDueAt('');
    } catch (requestError) {
      setError(
        (requestError as { status?: number }).status === 400
          ? 'Revise a descrição, a criticidade e o prazo da pendência.'
          : 'Não foi possível criar a pendência. Tente novamente.',
      );
    } finally {
      setIsCreatingPendingItem(false);
    }
  };

  const setPendingItemStatus = async (
    item: OperationDetail['pendingItems'][number],
    status: 'open' | 'resolved',
  ) => {
    if (!canEdit || updatingPendingItemId) return;
    setUpdatingPendingItemId(item.id);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/pending-items/${item.id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            expectedRevision: item.revision,
            status,
            resolutionNote: null,
          }),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        setOperation(await loadOperation());
        setError('Esta pendência foi alterada em outra sessão. A ficha foi atualizada.');
        return;
      }
      if (!response.ok) throw responseError(response);
      setOperation(await loadOperation());
    } catch {
      setError('Não foi possível atualizar a pendência. O estado anterior foi preservado.');
    } finally {
      setUpdatingPendingItemId(undefined);
    }
  };

  const recordDecision = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || !operation || isRecordingDecision || checklistRequests.current.size > 0) return;
    const result: CoordinationDecisionAttempt['result'] =
      operation.state === 'ready' ? 'reopened' : 'ready';
    const justification = decisionJustification.trim();
    const attempt =
      decisionAttempt?.result === result && decisionAttempt.justification === justification
        ? decisionAttempt
        : {
            decisionId: crypto.randomUUID(),
            idempotencyKey: crypto.randomUUID(),
            result,
            justification,
          };
    setDecisionAttempt(attempt);
    setIsRecordingDecision(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/coordination-decisions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(attempt),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as { code?: string } | null;
        setOperation(await loadOperation());
        setError(
          conflict?.code === 'OPERATION_CRITICAL_PENDING_OPEN'
            ? 'Resolva todas as pendências críticas antes de concluir a coordenação.'
            : conflict?.code === 'OPERATION_CHECKLIST_INCOMPLETE'
              ? 'Conclua todos os itens obrigatórios do checklist antes da decisão.'
              : conflict?.code === 'OPERATION_STATE_CONFLICT'
                ? 'O estado da operação mudou. A ficha foi atualizada para sua revisão.'
                : 'A decisão entrou em conflito com outra atualização. Revise a ficha.',
        );
        return;
      }
      if (!response.ok) throw responseError(response);
      const resultBody = (await response.json()) as CoordinationDecisionResponse;
      setOperation(resultBody.operation);
      setDecisionJustification('');
      setDecisionAttempt(undefined);
    } catch (requestError) {
      setError(
        (requestError as { status?: number }).status === 400
          ? 'Informe uma justificativa de pelo menos 10 caracteres.'
          : 'Não foi possível registrar a decisão. O estado anterior foi preservado.',
      );
    } finally {
      setIsRecordingDecision(false);
    }
  };

  const recordOperationalDecision = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || !operation || isRecordingOperationalDecision) return;
    const activeDecision = operation.operationalAuthorityDecisions[0];
    const hasActiveDecision = activeDecision && activeDecision.result !== 'revoked';
    const result: OperationalDecisionAttempt['result'] = hasActiveDecision
      ? 'revoked'
      : operationalResult;
    const scope = (hasActiveDecision ? activeDecision.scope : operationalScope).trim();
    const justification = operationalJustification.trim();
    const supersedesDecisionId = hasActiveDecision ? activeDecision.id : null;
    const previousMatches =
      operationalAttempt?.result === result &&
      operationalAttempt.scope === scope &&
      operationalAttempt.justification === justification &&
      operationalAttempt.supersedesDecisionId === supersedesDecisionId;
    const attempt: OperationalDecisionAttempt = previousMatches
      ? operationalAttempt
      : {
          decisionId: crypto.randomUUID(),
          idempotencyKey: crypto.randomUUID(),
          result,
          scope,
          justification,
          supersedesDecisionId,
        };
    setOperationalAttempt(attempt);
    setIsRecordingOperationalDecision(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/operational-decisions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(attempt),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as { code?: string } | null;
        setOperation(await loadOperation());
        setError(
          conflict?.code === 'OPERATION_COORDINATION_INCOMPLETE'
            ? 'Conclua a coordenação antes de registrar uma decisão autorizada.'
            : conflict?.code === 'OPERATION_PREREQUISITES_INCOMPLETE'
              ? 'As pré-condições calculadas não permitem registrar uma decisão autorizada.'
              : 'A decisão ativa ou o estado da operação mudou. A ficha foi atualizada.',
        );
        return;
      }
      if (!response.ok) throw responseError(response);
      const resultBody = (await response.json()) as CoordinationDecisionResponse;
      setOperation(resultBody.operation);
      setOperationalJustification('');
      setOperationalAttempt(undefined);
    } catch (requestError) {
      setError(
        (requestError as { status?: number }).status === 400
          ? 'Revise o escopo e informe uma justificativa de pelo menos 10 caracteres.'
          : 'Não foi possível registrar a decisão operacional. Nada foi inferido ou alterado.',
      );
    } finally {
      setIsRecordingOperationalDecision(false);
    }
  };

  const recordExecutionEvent = async (
    eventType: 'started' | 'departed' | 'arrived' | 'cancelled',
    operationLegId: string | null,
  ) => {
    if (!canEdit || !operation || isRecordingExecutionEvent) return;
    setIsRecordingExecutionEvent(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/execution-events`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            operationAttempt(
              attempts.current,
              `${organizationId}/${operationId}/execution`,
              {
                eventType,
                operationLegId,
                eventAt: executionTime ? fromTimeInput(executionTime, false) : null,
                note: executionNote.trim() || null,
              },
              () => ({
                eventId: crypto.randomUUID(),
                eventAt: executionTime
                  ? fromTimeInput(executionTime, false)
                  : new Date().toISOString(),
              }),
            ),
          ),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as { code?: string } | null;
        setOperation(await loadOperation());
        setError(
          conflict?.code === 'OPERATIONAL_AUTHORIZATION_REQUIRED'
            ? 'Registre uma decisão operacional autorizada antes de iniciar a execução.'
            : conflict?.code === 'EXECUTION_SEQUENCE_CONFLICT'
              ? 'Registre a chegada do trecho anterior e confira a ordem dos horários antes desta partida.'
              : conflict?.code === 'EXECUTION_TIME_CONFLICT'
                ? 'O horário deve ser igual ou posterior ao último marco deste trecho.'
                : conflict?.code === 'EXECUTION_LEG_CANCELLED'
                  ? 'Este trecho foi cancelado e não pode receber marcos de execução.'
                  : conflict?.code === 'EXECUTION_NO_ACTIVE_LEGS'
                    ? 'A operação não possui trechos ativos para iniciar a execução.'
                    : 'O estado da execução mudou. A ficha foi atualizada.',
        );
        return;
      }
      if (!response.ok) throw responseError(response);
      const result = (await response.json()) as { operation: OperationDetail };
      setOperation(result.operation);
      attempts.current.delete(`${organizationId}/${operationId}/execution`);
      setExecutionNote('');
      setExecutionTime('');
    } catch {
      setError(
        'Não foi possível confirmar o registro. Confira a ficha ou reenvie a mesma tentativa.',
      );
    } finally {
      setIsRecordingExecutionEvent(false);
    }
  };

  const createOccurrence = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || isRecordingOccurrence) return;
    setIsRecordingOccurrence(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/occurrences`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            operationAttempt(
              attempts.current,
              `${organizationId}/${operationId}/occurrence`,
              {
                operationLegId: occurrenceLegId || null,
                occurrenceType,
                severity: occurrenceSeverity,
                occurredAt: null,
                description: occurrenceDescription,
                justification: occurrenceJustification,
              },
              () => ({ occurrenceId: crypto.randomUUID(), occurredAt: new Date().toISOString() }),
            ),
          ),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw responseError(response);
      const result = (await response.json()) as { operation: OperationDetail };
      setOperation(result.operation);
      attempts.current.delete(`${organizationId}/${operationId}/occurrence`);
      setOccurrenceDescription('');
      setOccurrenceJustification('');
    } catch (requestError) {
      setError(
        (requestError as { status?: number }).status === 400
          ? 'Preencha a ocorrência e sua justificativa (mínimo de 10 caracteres).'
          : 'Não foi possível registrar a ocorrência. Tente novamente.',
      );
    } finally {
      setIsRecordingOccurrence(false);
    }
  };

  const closeOperation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || isClosingOperation || operation?.closure) return;
    setIsClosingOperation(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/closure`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            operationAttempt(
              attempts.current,
              `${organizationId}/${operationId}/closure`,
              {
                result: closureResult,
                summary: closureSummary,
                checklist: [
                  {
                    key: 'execution_recorded',
                    label: 'Execução registrada',
                    completed: closureChecks.execution_recorded,
                  },
                  {
                    key: 'occurrences_reviewed',
                    label: 'Ocorrências revisadas',
                    completed: closureChecks.occurrences_reviewed,
                  },
                  {
                    key: 'handover_completed',
                    label: 'Passagem concluída',
                    completed: closureChecks.handover_completed,
                  },
                ],
              },
              () => ({ closureId: crypto.randomUUID() }),
            ),
          ),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as { code?: string } | null;
        setOperation(await loadOperation());
        setError(
          conflict?.code === 'EXECUTION_INCOMPLETE'
            ? 'Conclua ou cancele a execução antes de encerrar a operação.'
            : conflict?.code === 'CLOSURE_CHECKLIST_INCOMPLETE'
              ? 'Conclua os três itens do checklist de encerramento.'
              : conflict?.code === 'CANCELLATION_OCCURRENCE_REQUIRED'
                ? 'Registre a ocorrência de cancelamento antes de encerrar.'
                : 'O encerramento entrou em conflito com outra atualização.',
        );
        return;
      }
      if (!response.ok) throw responseError(response);
      const result = (await response.json()) as { operation: OperationDetail };
      setOperation(result.operation);
      attempts.current.delete(`${organizationId}/${operationId}/closure`);
      setClosureSummary('');
    } catch {
      setError(
        'Não foi possível confirmar o encerramento. Confira a ficha ou reenvie a mesma tentativa.',
      );
    } finally {
      setIsClosingOperation(false);
    }
  };

  const createAmendment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || isCreatingAmendment || !operation?.closure) return;
    setIsCreatingAmendment(true);
    setError(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/amendments`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            operationAttempt(
              attempts.current,
              `${organizationId}/${operationId}/amendment`,
              {
                reason: amendmentReason,
                correction: amendmentCorrection,
              },
              () => ({ amendmentId: crypto.randomUUID() }),
            ),
          ),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw responseError(response);
      setOperation(await loadOperation());
      attempts.current.delete(`${organizationId}/${operationId}/amendment`);
      setAmendmentReason('');
      setAmendmentCorrection('');
    } catch {
      setError('Não foi possível registrar o adendo auditado.');
    } finally {
      setIsCreatingAmendment(false);
    }
  };

  const completedChecklistItems =
    operation?.checklist?.items.filter((item) => item.completed).length ?? 0;
  const totalChecklistItems = operation?.checklist?.items.length ?? 0;
  const hasOpenCriticalPending =
    operation?.pendingItems.some(
      (item) => item.status === 'open' && item.severity === 'critical',
    ) ?? false;
  const hasIncompleteRequiredChecklist =
    !operation?.checklist ||
    operation.checklist.items.some((item) => item.required && !item.completed);
  const canTransitionCoordination =
    operation?.state === 'ready' ||
    operation?.state === 'draft' ||
    operation?.state === 'planned' ||
    operation?.state === 'coordinating';
  const canSubmitDecision =
    canEdit &&
    !isSavingChecklist &&
    canTransitionCoordination &&
    decisionJustification.trim().length >= 10 &&
    (operation?.state === 'ready' || (!hasOpenCriticalPending && !hasIncompleteRequiredChecklist));
  const activeOperationalDecision = operation?.operationalAuthorityDecisions[0];
  const hasActiveOperationalDecision =
    activeOperationalDecision !== undefined && activeOperationalDecision.result !== 'revoked';
  const hasStartedExecution = operation?.executionStatus !== 'not_started';
  const isExecuting = operation?.executionStatus === 'in_progress';
  const closureBlocked =
    operation?.executionStatus !== 'completed' && operation?.executionStatus !== 'cancelled';
  const cancellationOccurrenceMissing =
    operation?.executionStatus === 'cancelled' &&
    !operation.occurrences.some((item) => item.occurrenceType === 'cancellation');
  const executionCanStart =
    canEdit &&
    operation?.state !== 'closed' &&
    operation?.state !== 'cancelled' &&
    operation?.executionStatus === 'not_started' &&
    hasActiveOperationalDecision &&
    activeOperationalDecision?.result === 'authorized';

  const nextStep = operationNextStep({
    closed: Boolean(operation?.closure),
    executionStatus: operation?.executionStatus ?? 'not_started',
    coordinationState: operation?.state ?? 'draft',
    openPendingCount:
      operation?.pendingItems.filter(
        (item) => item.status === 'open' && item.severity === 'critical',
      ).length ?? 0,
    checklistComplete: !hasIncompleteRequiredChecklist && !isSavingChecklist,
    authorityResult: activeOperationalDecision?.result,
  });
  return (
    <section
      className="operationDetail"
      id="operation-detail"
      aria-labelledby="operation-detail-title"
    >
      <header className="workflowHeader">
        <div>
          <span className="sectionEyebrow">Coordenação de voo</span>
          <h2 id="operation-detail-title">
            {operation?.missionName ?? operation?.referenceCode ?? 'Carregando operação…'}
          </h2>
          {operation ? (
            <p>
              {operation.referenceCode} · {operationStateLabels[operation.state] ?? operation.state}{' '}
              · Horários em {Intl.DateTimeFormat().resolvedOptions().timeZone.replaceAll('_', ' ')}
            </p>
          ) : null}
        </div>
        <button type="button" onClick={onClose}>
          Voltar às operações
        </button>
      </header>

      {error ? (
        <div className="inlineError" role="alert">
          {error}
        </div>
      ) : null}
      {!operation && !error ? (
        <div className="workflowLoading" role="status">
          Reabrindo o registro…
        </div>
      ) : null}

      {operation ? (
        <>
          <div className="operationStateStrip" aria-label="Situação da operação">
            <div>
              <span>Coordenação</span>
              <strong>{operationStateLabels[operation.state] ?? operation.state}</strong>
            </div>
            <div>
              <span>Decisão operacional</span>
              <strong>
                {activeOperationalDecision?.result === 'authorized'
                  ? 'Autorizada'
                  : activeOperationalDecision?.result === 'not_authorized'
                    ? 'Não autorizada'
                    : 'Sem decisão ativa'}
              </strong>
            </div>
            <div>
              <span>Execução</span>
              <strong>
                {operation.executionStatus === 'not_started'
                  ? 'Não iniciada'
                  : operation.executionStatus === 'in_progress'
                    ? 'Em execução'
                    : operation.executionStatus === 'completed'
                      ? 'Concluída'
                      : operation.executionStatus === 'cancelled'
                        ? 'Cancelada'
                        : operation.executionStatus}
              </strong>
            </div>
          </div>
          <nav className="operationFunctionNav" aria-label="Etapas da operação">
            {operationSections.map(([id, label], index) => (
              <button
                key={id}
                type="button"
                aria-current={activeSection === id ? 'step' : undefined}
                aria-controls={id}
                onClick={() => selectSection(id)}
              >
                <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                {label}
                {id === 'operation-pending' &&
                operation.pendingItems.some((item) => item.status === 'open') ? (
                  <b>{operation.pendingItems.filter((item) => item.status === 'open').length}</b>
                ) : null}
              </button>
            ))}
          </nav>
          <OperationPlanning
            organizationId={organizationId}
            operationId={operationId}
            active={active && activeSection === 'operation-planning'}
            onExpiredSession={onExpiredSession}
            onChanged={async () => {
              setOperation(await loadOperation());
            }}
          />
          <div hidden={activeSection !== 'operation-resources'}>
            <OperationResources
              canEdit={canEdit && !operation.closure}
              legs={operation.legs}
              operationId={operationId}
              organizationId={organizationId}
              onExpiredSession={onExpiredSession}
            />
          </div>
          <div hidden={activeSection !== 'operation-summary'}>
            <div className="nextStepCard">
              <div>
                <span className="sectionEyebrow">Próximo passo</span>
                <strong>{nextStep.title}</strong>
                <p>
                  {operation.closure
                    ? 'Consulte o encerramento e registre uma retificação quando necessário.'
                    : 'Coordenação concluída não substitui a autorização operacional.'}
                </p>
              </div>
              <button type="button" onClick={() => selectSection(nextStep.section)}>
                Abrir etapa →
              </button>
            </div>
          </div>

          <div
            className="operationFacts"
            id="operation-summary"
            hidden={activeSection !== 'operation-summary'}
          >
            <div>
              <span>Aeronave</span>
              <strong>{operation.aircraft?.registrationMark ?? 'Atribuição pendente'}</strong>
              <small>{operation.aircraft?.model ?? 'Sem aeronave definida'}</small>
            </div>
            <div>
              <span>Coordenador</span>
              <strong>{operation.coordinator?.name ?? 'Não informado'}</strong>
              <small>{operation.base?.name ?? 'Base não definida'}</small>
            </div>
            <div>
              <span>Última atualização</span>
              <strong>{formatDateTime(operation.updatedAt)}</strong>
              <small>{operation.legs.length} trecho(s)</small>
            </div>
          </div>

          <ol className="operationTimeline" hidden={activeSection !== 'operation-summary'}>
            {operation.legs.map((leg) => (
              <li key={leg.id}>
                <span className="legSequence">{leg.sequence}</span>
                <div>
                  <strong>
                    {leg.originCode ?? leg.originName} →{' '}
                    {leg.destinationCode ?? leg.destinationName}
                  </strong>
                  <span>
                    {leg.originName} → {leg.destinationName}
                  </span>
                  {leg.originCoordinates && leg.destinationCoordinates ? (
                    <LegDistance
                      origin={leg.originCoordinates}
                      destination={leg.destinationCoordinates}
                    />
                  ) : null}
                  {(['origin', 'destination'] as const).map((side) => {
                    const point = leg[`${side}Coordinates`];
                    return point && leg[`${side}Code`] === 'ZZZZ' ? (
                      <a
                        key={side}
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${point.latitude},${point.longitude}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {side === 'origin' ? 'Origem' : 'Destino'}: {point.latitude.toFixed(6)},{' '}
                        {point.longitude.toFixed(6)} · Google Maps ↗
                      </a>
                    ) : null;
                  })}
                </div>
                <time dateTime={leg.plannedDeparture}>
                  {formatDateTime(leg.plannedDeparture)}
                  <span>até {formatDateTime(leg.plannedArrival)}</span>
                  {leg.revisedDeparture || leg.revisedArrival ? (
                    <span>
                      Revisado: {leg.revisedDeparture ? formatDateTime(leg.revisedDeparture) : '—'}{' '}
                      até {leg.revisedArrival ? formatDateTime(leg.revisedArrival) : '—'}
                    </span>
                  ) : null}
                  {leg.actualDeparture || leg.actualArrival ? (
                    <span>
                      Realizado: {leg.actualDeparture ? formatDateTime(leg.actualDeparture) : '—'}{' '}
                      até {leg.actualArrival ? formatDateTime(leg.actualArrival) : '—'}
                    </span>
                  ) : null}
                </time>
              </li>
            ))}
          </ol>

          <section
            className="operationPending"
            hidden={activeSection !== 'operation-pending'}
            id="operation-pending"
            aria-labelledby="operation-pending-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Pontos para continuidade</span>
                <h3 id="operation-pending-title">Pendências da operação</h3>
                <p>
                  {operation.pendingItems.filter((item) => item.status === 'open').length} abertas ·{' '}
                  {operation.pendingItems.filter((item) => item.status === 'resolved').length}{' '}
                  resolvidas
                </p>
              </div>
            </header>

            {operation.pendingItems.length > 0 ? (
              <ul className="operationPendingList">
                {operation.pendingItems.map((item) => (
                  <li
                    className={`pendingSeverity-${item.severity}${item.status === 'resolved' ? ' pendingResolved' : ''}`}
                    key={item.id}
                  >
                    <div>
                      <span>
                        {item.severity === 'critical'
                          ? 'Crítica'
                          : item.severity === 'warning'
                            ? 'Atenção'
                            : 'Informativa'}
                      </span>
                      <strong>{item.title}</strong>
                      {item.detail ? <p>{item.detail}</p> : null}
                      <small>
                        {item.responsible?.name ?? 'Sem responsável'}
                        {item.dueAt ? ` · até ${formatDateTime(item.dueAt)}` : ''}
                        {item.resolvedBy ? ` · resolvida por ${item.resolvedBy.name}` : ''}
                      </small>
                    </div>
                    {canEdit ? (
                      <button
                        disabled={updatingPendingItemId !== undefined || Boolean(operation.closure)}
                        type="button"
                        onClick={() =>
                          void setPendingItemStatus(
                            item,
                            item.status === 'open' ? 'resolved' : 'open',
                          )
                        }
                      >
                        {item.status === 'open' ? 'Marcar resolvida' : 'Reabrir'}
                      </button>
                    ) : (
                      <span>{item.status === 'open' ? 'Aberta' : 'Resolvida'}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="handoffNoPending">
                Nenhuma pendência registrada. Não é necessário criar uma para avançar: conclua o
                checklist obrigatório abaixo e continue para a coordenação.
              </p>
            )}

            {canEdit && !operation.closure ? (
              <form className="pendingItemForm" onSubmit={(event) => void createPendingItem(event)}>
                <label className="pendingTitleField">
                  <span>Título</span>
                  <input
                    maxLength={240}
                    minLength={3}
                    placeholder="Ex.: Confirmar slot de chegada"
                    required
                    value={pendingTitle}
                    onChange={(event) => setPendingTitle(event.target.value)}
                  />
                </label>
                <label>
                  <span>Criticidade</span>
                  <select
                    value={pendingSeverity}
                    onChange={(event) => setPendingSeverity(event.target.value)}
                  >
                    <option value="critical">Crítica</option>
                    <option value="warning">Atenção</option>
                    <option value="informational">Informativa</option>
                  </select>
                </label>
                <label>
                  <span>Responsável</span>
                  <select
                    value={pendingResponsibleUserId}
                    onChange={(event) => setPendingResponsibleUserId(event.target.value)}
                  >
                    <option value="">Sem responsável</option>
                    {members.map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Prazo</span>
                  <input
                    type="datetime-local"
                    value={pendingDueAt}
                    onChange={(event) => setPendingDueAt(event.target.value)}
                  />
                </label>
                <label className="pendingDetailField">
                  <span>Detalhe</span>
                  <textarea
                    maxLength={2000}
                    placeholder="Contexto necessário para o próximo coordenador."
                    rows={2}
                    value={pendingDetail}
                    onChange={(event) => setPendingDetail(event.target.value)}
                  />
                </label>
                <button className="primaryButton" disabled={isCreatingPendingItem} type="submit">
                  {isCreatingPendingItem ? 'Adicionando…' : 'Adicionar pendência'}
                </button>
              </form>
            ) : (
              <p className="checklistReadOnly">Seu perfil permite consultar, sem alterar.</p>
            )}
          </section>

          <div hidden={activeSection !== 'operation-pending'}>
            {operation.checklist ? (
              <section
                className="operationChecklist"
                id="operation-checklist"
                aria-labelledby="operation-checklist-title"
              >
                <header>
                  <div>
                    <span className="sectionEyebrow">Conferência da operação</span>
                    <h3 id="operation-checklist-title">{operation.checklist.templateName}</h3>
                    <p>
                      Versão {operation.checklist.templateVersion} · {completedChecklistItems} de{' '}
                      {totalChecklistItems} itens concluídos
                    </p>
                  </div>
                  <span
                    className={
                      completedChecklistItems === totalChecklistItems
                        ? 'checklistProgress checklistProgress-complete'
                        : 'checklistProgress'
                    }
                  >
                    {completedChecklistItems === totalChecklistItems ? 'Completo' : 'Em andamento'}
                  </span>
                </header>
                <ul className="checklistItems">
                  {operation.checklist.items.map((item) => (
                    <li
                      className={
                        (checklistChanges[item.id] ?? item.completed) ? 'completed' : undefined
                      }
                      key={item.id}
                    >
                      <label>
                        <input
                          checked={checklistChanges[item.id] ?? item.completed}
                          disabled={
                            !canEdit ||
                            checklistChanges[item.id] !== undefined ||
                            Boolean(operation.closure)
                          }
                          type="checkbox"
                          onChange={(event) =>
                            void setChecklistItemCompletion(item, event.target.checked)
                          }
                        />
                        <span>
                          <strong>{item.title}</strong>
                          <small>
                            {item.required ? 'Obrigatório' : 'Opcional'}
                            {checklistChanges[item.id] !== undefined
                              ? ' · salvando…'
                              : item.completedBy
                                ? ` · concluído por ${item.completedBy.name}`
                                : ' · aguardando conferência'}
                          </small>
                        </span>
                      </label>
                      <span className="checklistSequence">{item.sequence}</span>
                    </li>
                  ))}
                </ul>
                {canEdit && !operation.closure ? (
                  <div className="checklistNextStep">
                    <p role="status">
                      {isSavingChecklist
                        ? 'Salvando suas marcações… Você pode conferir os outros itens.'
                        : hasIncompleteRequiredChecklist
                          ? 'Conclua os itens obrigatórios para continuar.'
                          : hasOpenCriticalPending
                            ? 'Resolva as pendências críticas para continuar.'
                            : 'Checklist obrigatório concluído. Você pode seguir para a próxima etapa.'}
                    </p>
                    <button
                      type="button"
                      className="primaryButton"
                      disabled={isSavingChecklist || nextStep.section === 'operation-pending'}
                      onClick={() => selectSection(nextStep.section)}
                    >
                      {operation.state === 'ready'
                        ? 'Continuar para a próxima etapa'
                        : 'Continuar para Coordenação'}
                    </button>
                  </div>
                ) : null}
                {!canEdit ? (
                  <p className="checklistReadOnly">Seu perfil permite consultar, sem alterar.</p>
                ) : null}
              </section>
            ) : (
              <div className="inlineError" role="status">
                Esta operação ainda não possui checklist aplicado.
              </div>
            )}
          </div>
          <section
            className="coordinationDecision"
            hidden={activeSection !== 'coordination-decision'}
            id="coordination-decision"
            aria-labelledby="coordination-decision-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Conferências concluídas</span>
                <h3 id="coordination-decision-title">Conclusão da coordenação</h3>
                <p>
                  Registra quem decidiu, a função efetiva, a justificativa e a mudança de estado.
                  Não representa autorização de voo.
                </p>
              </div>
              <span className="tenantStatus">Histórico imutável</span>
            </header>

            {operation.coordinationDecisions.length > 0 ? (
              <ol className="coordinationDecisionHistory">
                {operation.coordinationDecisions.map((decision) => (
                  <li key={decision.id}>
                    <div>
                      <strong>
                        {decision.result === 'ready'
                          ? 'Coordenação concluída'
                          : 'Coordenação reaberta'}
                      </strong>
                      <span>
                        {decision.decidedBy.name} ·{' '}
                        {decision.authorityRoleKey === 'organization_administrator'
                          ? 'Administrador da Organização'
                          : 'Coordenador'}
                      </span>
                      <p>{decision.justification}</p>
                    </div>
                    <time dateTime={decision.decidedAt}>
                      {formatDateTime(decision.decidedAt)}
                      <span>
                        {operationStateLabels[decision.previousState] ?? decision.previousState} →{' '}
                        {operationStateLabels[decision.resultingState] ?? decision.resultingState}
                      </span>
                    </time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="checklistReadOnly">Nenhuma decisão registrada até o momento.</p>
            )}

            {canEdit && canTransitionCoordination ? (
              <form
                className="coordinationDecisionForm"
                onSubmit={(event) => void recordDecision(event)}
              >
                <label>
                  <span>Justificativa da decisão</span>
                  <textarea
                    maxLength={2000}
                    minLength={10}
                    placeholder={
                      operation.state === 'ready'
                        ? 'Explique por que a coordenação precisa ser reaberta.'
                        : 'Resuma as conferências que sustentam a conclusão.'
                    }
                    required
                    rows={3}
                    value={decisionJustification}
                    onChange={(event) => setDecisionJustification(event.target.value)}
                  />
                </label>
                {operation.state !== 'ready' &&
                (hasOpenCriticalPending || hasIncompleteRequiredChecklist) ? (
                  <p className="decisionBlocked" role="status">
                    {hasOpenCriticalPending
                      ? 'Há pendência crítica aberta.'
                      : 'Há item obrigatório do checklist pendente.'}{' '}
                    Regularize esses itens para concluir a coordenação.
                  </p>
                ) : null}
                <button
                  className="primaryButton"
                  disabled={!canSubmitDecision || isRecordingDecision}
                  type="submit"
                >
                  {isRecordingDecision
                    ? 'Registrando…'
                    : operation.state === 'ready'
                      ? 'Reabrir coordenação'
                      : 'Concluir coordenação'}
                </button>
              </form>
            ) : !canEdit ? (
              <p className="checklistReadOnly">
                Seu perfil permite consultar o histórico, sem decidir.
              </p>
            ) : (
              <p className="checklistReadOnly">
                Este estado operacional não permite concluir ou reabrir a coordenação.
              </p>
            )}
          </section>

          <section
            className="operationalDecision"
            hidden={activeSection !== 'operational-decision'}
            id="operational-decision"
            aria-labelledby="operational-decision-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Estado independente</span>
                <h3 id="operational-decision-title">Decisão operacional</h3>
                <p>
                  Não é inferida pela coordenação e não inicia a execução. Cada resultado possui
                  escopo, justificativa, ator e autoridade preservados.
                </p>
              </div>
              <span className="tenantStatus">
                {hasActiveOperationalDecision
                  ? activeOperationalDecision.result === 'authorized'
                    ? 'Autorizada'
                    : 'Não autorizada'
                  : 'Não registrada'}
              </span>
            </header>

            {operation.operationalAuthorityDecisions.length > 0 ? (
              <ol className="coordinationDecisionHistory operationalDecisionHistory">
                {operation.operationalAuthorityDecisions.map((decision) => (
                  <li key={decision.id}>
                    <div>
                      <strong>
                        {decision.result === 'authorized'
                          ? 'Autorizada'
                          : decision.result === 'not_authorized'
                            ? 'Não autorizada'
                            : 'Revogada'}
                      </strong>
                      <span>
                        {decision.decidedBy.name} ·{' '}
                        {decision.authorityRoleKey === 'organization_administrator'
                          ? 'Administrador da Organização'
                          : 'Coordenador'}
                      </span>
                      <p>
                        <b>Escopo:</b> {decision.scope}
                      </p>
                      <p>{decision.justification}</p>
                    </div>
                    <time dateTime={decision.decidedAt}>{formatDateTime(decision.decidedAt)}</time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="checklistReadOnly">Nenhuma decisão operacional foi registrada.</p>
            )}

            {canEdit && !operation.closure ? (
              <form
                className="operationalDecisionForm"
                onSubmit={(event) => void recordOperationalDecision(event)}
              >
                {!hasActiveOperationalDecision ? (
                  <>
                    <label>
                      <span>Resultado</span>
                      <select
                        value={operationalResult}
                        onChange={(event) =>
                          setOperationalResult(
                            event.target.value as 'authorized' | 'not_authorized',
                          )
                        }
                      >
                        <option value="authorized">Autorizada</option>
                        <option value="not_authorized">Não autorizada</option>
                      </select>
                    </label>
                    <label className="operationalScopeField">
                      <span>Escopo explícito</span>
                      <input
                        maxLength={1000}
                        minLength={10}
                        required
                        value={operationalScope}
                        onChange={(event) => setOperationalScope(event.target.value)}
                      />
                    </label>
                  </>
                ) : (
                  <p className="decisionBlocked">
                    Para registrar outro resultado, revogue primeiro a decisão ativa. O registro
                    anterior será preservado.
                  </p>
                )}
                <label className="operationalJustificationField">
                  <span>
                    {hasActiveOperationalDecision
                      ? 'Justificativa da revogação'
                      : 'Justificativa da decisão'}
                  </span>
                  <textarea
                    maxLength={2000}
                    minLength={10}
                    required
                    rows={3}
                    value={operationalJustification}
                    onChange={(event) => setOperationalJustification(event.target.value)}
                  />
                </label>
                <button
                  className={hasActiveOperationalDecision ? 'dangerButton' : 'primaryButton'}
                  disabled={
                    isRecordingOperationalDecision || operationalJustification.trim().length < 10
                  }
                  type="submit"
                >
                  {isRecordingOperationalDecision
                    ? 'Registrando…'
                    : hasActiveOperationalDecision
                      ? 'Revogar decisão ativa'
                      : 'Registrar decisão operacional'}
                </button>
              </form>
            ) : (
              <p className="checklistReadOnly">Seu perfil permite consultar, sem decidir.</p>
            )}
          </section>

          <section
            className="operationExecution"
            hidden={activeSection !== 'operation-execution'}
            id="operation-execution"
            aria-labelledby="operation-execution-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Operação agora</span>
                <h3 id="operation-execution-title">Execução operacional</h3>
                <p>
                  O realizado é registrado por evento e não substitui os horários planejados ou
                  revisados.
                </p>
              </div>
              <span className="tenantStatus">
                {executionStatusLabels[operation.executionStatus] ?? operation.executionStatus}
              </span>
            </header>

            <div className="executionLegs">
              {operation.legs.map((leg) => {
                const hasDeparted = leg.actualDeparture !== null;
                const hasArrived = leg.actualArrival !== null;
                return (
                  <article key={leg.id} className="executionLeg">
                    <div>
                      <strong>
                        {leg.originCode ?? leg.originName} →{' '}
                        {leg.destinationCode ?? leg.destinationName}
                      </strong>
                      <small>
                        Planejado: {formatDateTime(leg.plannedDeparture)} →{' '}
                        {formatDateTime(leg.plannedArrival)}
                      </small>
                      <small>
                        Realizado:{' '}
                        {leg.actualDeparture
                          ? formatDateTime(leg.actualDeparture)
                          : 'partida pendente'}{' '}
                        →{' '}
                        {leg.actualArrival ? formatDateTime(leg.actualArrival) : 'chegada pendente'}
                      </small>
                    </div>
                    {leg.cancelledAt ? (
                      <p className="checklistReadOnly">
                        Trecho cancelado · {formatDateTime(leg.cancelledAt)}
                      </p>
                    ) : null}
                    {canEdit && !operation.closure && !leg.cancelledAt ? (
                      <div className="executionActions">
                        <button
                          disabled={!isExecuting || hasDeparted || isRecordingExecutionEvent}
                          type="button"
                          onClick={() => void recordExecutionEvent('departed', leg.id)}
                        >
                          Registrar partida
                        </button>
                        <button
                          disabled={
                            !isExecuting || !hasDeparted || hasArrived || isRecordingExecutionEvent
                          }
                          type="button"
                          onClick={() => void recordExecutionEvent('arrived', leg.id)}
                        >
                          Registrar chegada
                        </button>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>

            {canEdit && !operation.closure ? (
              <div className="executionControls">
                <label>
                  <span>Data e hora do marco</span>
                  <input
                    type="datetime-local"
                    aria-label="Data e hora do marco"
                    step="1"
                    value={executionTime}
                    onChange={(event) => setExecutionTime(event.target.value)}
                  />
                  <small>
                    Fuso: {Intl.DateTimeFormat().resolvedOptions().timeZone}. Deixe vazio para usar
                    o instante do primeiro envio.
                  </small>
                </label>
                <label>
                  <span>Nota do marco (opcional)</span>
                  <input
                    maxLength={2000}
                    value={executionNote}
                    onChange={(event) => setExecutionNote(event.target.value)}
                  />
                </label>
                {!hasStartedExecution ? (
                  <button
                    className="primaryButton"
                    disabled={!executionCanStart || isRecordingExecutionEvent}
                    type="button"
                    onClick={() => void recordExecutionEvent('started', null)}
                  >
                    {executionCanStart ? 'Iniciar execução' : 'Aguardando autorização operacional'}
                  </button>
                ) : null}
                {hasStartedExecution && operation.executionStatus === 'in_progress' ? (
                  <button
                    className="dangerButton"
                    disabled={isRecordingExecutionEvent || operation.legs.length === 0}
                    type="button"
                    onClick={() => selectSection('operation-planning')}
                  >
                    Cancelar execução
                  </button>
                ) : null}
              </div>
            ) : !canEdit ? (
              <p className="checklistReadOnly">
                Seu perfil permite consultar, sem registrar marcos.
              </p>
            ) : null}

            {operation.executionEvents.length > 0 ? (
              <ol className="coordinationDecisionHistory executionHistory">
                {operation.executionEvents.map((event) => (
                  <li key={event.id}>
                    <div>
                      <strong>{executionEventLabels[event.eventType] ?? event.eventType}</strong>
                      <span>{event.recordedBy.name}</span>
                      {event.note ? <p>{event.note}</p> : null}
                    </div>
                    <time dateTime={event.eventAt}>{formatDateTime(event.eventAt)}</time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="checklistReadOnly">Nenhum marco de execução registrado.</p>
            )}
          </section>

          <section
            className="operationOccurrences"
            hidden={activeSection !== 'operation-occurrences'}
            id="operation-occurrences"
            aria-labelledby="operation-occurrences-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Exceções e desvios</span>
                <h3 id="operation-occurrences-title">Ocorrências da operação</h3>
                <p>
                  Desvios permanecem associados ao trecho, ao instante e à justificativa humana.
                </p>
              </div>
              <span className="tenantStatus">{operation.occurrences.length} registrada(s)</span>
            </header>

            {operation.occurrences.length > 0 ? (
              <ol className="coordinationDecisionHistory occurrenceHistory">
                {operation.occurrences.map((occurrence) => (
                  <li key={occurrence.id}>
                    <div>
                      <strong>
                        {occurrenceTypeLabels[occurrence.occurrenceType] ??
                          occurrence.occurrenceType}{' '}
                        · {occurrence.severity}
                      </strong>
                      <span>{occurrence.reportedBy.name}</span>
                      <p>{occurrence.description}</p>
                      <small>Justificativa: {occurrence.justification}</small>
                    </div>
                    <time dateTime={occurrence.occurredAt}>
                      {formatDateTime(occurrence.occurredAt)}
                    </time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="checklistReadOnly">Nenhuma ocorrência registrada.</p>
            )}

            {canEdit && !operation.closure ? (
              <form className="occurrenceForm" onSubmit={(event) => void createOccurrence(event)}>
                <label>
                  <span>Tipo</span>
                  <select
                    value={occurrenceType}
                    onChange={(event) => setOccurrenceType(event.target.value)}
                  >
                    {Object.entries(occurrenceTypeLabels).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Severidade</span>
                  <select
                    value={occurrenceSeverity}
                    onChange={(event) => setOccurrenceSeverity(event.target.value)}
                  >
                    <option value="informational">Informativa</option>
                    <option value="warning">Atenção</option>
                    <option value="critical">Crítica</option>
                  </select>
                </label>
                <label>
                  <span>Trecho</span>
                  <select
                    value={occurrenceLegId}
                    onChange={(event) => setOccurrenceLegId(event.target.value)}
                  >
                    <option value="">Operação inteira</option>
                    {operation.legs.map((leg) => (
                      <option key={leg.id} value={leg.id}>
                        Trecho {leg.sequence}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="occurrenceDescriptionField">
                  <span>Descrição</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={2}
                    value={occurrenceDescription}
                    onChange={(event) => setOccurrenceDescription(event.target.value)}
                  />
                </label>
                <label className="occurrenceJustificationField">
                  <span>Justificativa</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={2}
                    value={occurrenceJustification}
                    onChange={(event) => setOccurrenceJustification(event.target.value)}
                  />
                </label>
                <button className="primaryButton" disabled={isRecordingOccurrence} type="submit">
                  {isRecordingOccurrence ? 'Registrando…' : 'Registrar ocorrência'}
                </button>
              </form>
            ) : !canEdit ? (
              <p className="checklistReadOnly">
                Seu perfil permite consultar, sem registrar ocorrências.
              </p>
            ) : null}
          </section>

          <section
            className="operationClosure"
            hidden={activeSection !== 'operation-closure'}
            id="operation-closure"
            aria-labelledby="operation-closure-title"
          >
            <header>
              <div>
                <span className="sectionEyebrow">Conferência final</span>
                <h3 id="operation-closure-title">Encerramento do voo</h3>
                <p>Encerrar não apaga o planejado, os realizados ou o histórico de ocorrências.</p>
              </div>
              <span className="tenantStatus">{operation.closure ? 'Encerrada' : 'Em aberto'}</span>
            </header>

            {operation.closure ? (
              <div className="closureSummary">
                <strong>
                  {operation.closure.result === 'cancelled'
                    ? 'Execução cancelada'
                    : 'Execução concluída'}
                </strong>
                <p>{operation.closure.summary}</p>
                <small>
                  Encerrada por {operation.closure.closedBy.name} em{' '}
                  {formatDateTime(operation.closure.closedAt)}.
                </small>
              </div>
            ) : canEdit ? (
              <form className="closureForm" onSubmit={(event) => void closeOperation(event)}>
                <p>
                  Resultado:{' '}
                  <strong>
                    {closureBlocked
                      ? 'Execução pendente'
                      : closureResult === 'cancelled'
                        ? 'Cancelada'
                        : 'Concluída'}
                  </strong>
                </p>
                {closureBlocked ? (
                  <p role="status">
                    Registre a execução ou o cancelamento antes de encerrar.{' '}
                    <button type="button" onClick={() => selectSection('operation-execution')}>
                      Ir à execução
                    </button>
                  </p>
                ) : null}
                {cancellationOccurrenceMissing ? (
                  <p role="status">
                    Registre a ocorrência de cancelamento antes de encerrar.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setOccurrenceType('cancellation');
                        selectSection('operation-occurrences');
                      }}
                    >
                      Registrar ocorrência de cancelamento
                    </button>
                  </p>
                ) : null}
                <fieldset>
                  <legend>Checklist de encerramento</legend>
                  <label>
                    <input
                      type="checkbox"
                      checked={closureChecks.execution_recorded}
                      onChange={(event) =>
                        setClosureChecks((current) => ({
                          ...current,
                          execution_recorded: event.target.checked,
                        }))
                      }
                    />{' '}
                    Execução registrada
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={closureChecks.occurrences_reviewed}
                      onChange={(event) =>
                        setClosureChecks((current) => ({
                          ...current,
                          occurrences_reviewed: event.target.checked,
                        }))
                      }
                    />{' '}
                    Ocorrências revisadas
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={closureChecks.handover_completed}
                      onChange={(event) =>
                        setClosureChecks((current) => ({
                          ...current,
                          handover_completed: event.target.checked,
                        }))
                      }
                    />{' '}
                    Passagem concluída
                  </label>
                </fieldset>
                <label className="closureSummaryField">
                  <span>Resumo final</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={3}
                    value={closureSummary}
                    onChange={(event) => setClosureSummary(event.target.value)}
                  />
                </label>
                <button
                  className="primaryButton"
                  disabled={
                    isClosingOperation ||
                    closureBlocked ||
                    cancellationOccurrenceMissing ||
                    !Object.values(closureChecks).every(Boolean) ||
                    closureSummary.trim().length < 10
                  }
                  type="submit"
                >
                  {isClosingOperation ? 'Encerrando…' : 'Encerrar operação'}
                </button>
              </form>
            ) : (
              <p className="checklistReadOnly">
                Seu perfil permite consultar o encerramento, sem alterar.
              </p>
            )}

            {operation.amendments.length > 0 ? (
              <ol className="coordinationDecisionHistory amendmentHistory">
                {operation.amendments.map((amendment) => (
                  <li key={amendment.id}>
                    <div>
                      <strong>Adendo auditado</strong>
                      <span>{amendment.createdBy.name}</span>
                      <p>
                        <b>Motivo:</b> {amendment.reason}
                      </p>
                      <p>{amendment.correction}</p>
                    </div>
                    <time dateTime={amendment.createdAt}>
                      {formatDateTime(amendment.createdAt)}
                    </time>
                  </li>
                ))}
              </ol>
            ) : null}

            {operation.closure && canEdit ? (
              <form className="amendmentForm" onSubmit={(event) => void createAmendment(event)}>
                <label>
                  <span>Motivo do adendo</span>
                  <input
                    required
                    minLength={10}
                    maxLength={1000}
                    value={amendmentReason}
                    onChange={(event) => setAmendmentReason(event.target.value)}
                  />
                </label>
                <label>
                  <span>Correção/observação</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={2}
                    value={amendmentCorrection}
                    onChange={(event) => setAmendmentCorrection(event.target.value)}
                  />
                </label>
                <button className="secondaryButton" disabled={isCreatingAmendment} type="submit">
                  {isCreatingAmendment ? 'Registrando…' : 'Registrar adendo auditado'}
                </button>
              </form>
            ) : null}
          </section>
          <OperationReport
            organizationId={organizationId}
            operationId={operationId}
            active={active && activeSection === 'operation-report'}
            revision={operation.updatedAt + operation.amendments.length}
            onExpiredSession={onExpiredSession}
          />
        </>
      ) : null}
    </section>
  );
}
